// API wrapper services for standardized fetch and axios requests
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Standard headers for internal API calls
const getStandardHeaders = (additionalHeaders = {}) => ({
  'Content-Type': 'application/json',
  'ngrok-skip-browser-warning': 'true',
  ...additionalHeaders,
});

/**
 * Wrapper around fetch API for internal service calls
 * @param {string} endpoint - API endpoint path (without base URL)
 * @param {Object} options - Fetch options
 * @returns {Promise} - Fetch promise
 */
export const apiFetch = async (endpoint, options = {}) => {
  // Ensure endpoint doesn't start with a slash if the base URL ends with one
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  
  // Handle query parameters if they exist in options
  let url = `${API_BASE_URL}/${normalizedEndpoint}`;
  if (options.params) {
    const searchParams = new URLSearchParams();
    Object.entries(options.params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value);
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      url = `${url}?${queryString}`;
    }
    
    // Remove params from options to avoid conflict with fetch API
    const { params, ...restOptions } = options;
    options = restOptions;
  }
  
  // Merge provided headers with standard headers
  const headers = {
    ...getStandardHeaders(),
    ...(options.headers || {}),
  };
  
  return fetch(url, {
    ...options,
    headers,
  });
};

/**
 * GET request wrapper using the fetch API
 * @param {string} endpoint - API endpoint path (without base URL)
 * @param {Object} options - Additional fetch options
 * @returns {Promise<any>} - Parsed JSON response
 */
export const apiGet = async (endpoint, options = {}) => {
  const response = await apiFetch(endpoint, {
    method: 'GET',
    ...options,
  });
  
  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }
  
  return response.json();
};

/**
 * POST request wrapper using the fetch API
 * @param {string} endpoint - API endpoint path (without base URL)
 * @param {Object} data - Request body data
 * @param {Object} options - Additional fetch options
 * @returns {Promise<any>} - Parsed JSON response
 */
export const apiPost = async (endpoint, data, options = {}) => {
  const response = await apiFetch(endpoint, {
    method: 'POST',
    body: JSON.stringify(data),
    ...options,
  });
  
  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }
  
  return response.json();
};

/**
 * PUT request wrapper using the fetch API
 * @param {string} endpoint - API endpoint path (without base URL)
 * @param {Object} data - Request body data
 * @param {Object} options - Additional fetch options
 * @returns {Promise<any>} - Parsed JSON response
 */
export const apiPut = async (endpoint, data, options = {}) => {
  const response = await apiFetch(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data),
    ...options,
  });
  
  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }
  
  return response.json();
};

/**
 * DELETE request wrapper using the fetch API
 * @param {string} endpoint - API endpoint path (without base URL)
 * @param {Object} options - Additional fetch options
 * @returns {Promise<any>} - Parsed JSON response
 */
export const apiDelete = async (endpoint, options = {}) => {
  const response = await apiFetch(endpoint, {
    method: 'DELETE',
    ...options,
  });
  
  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }
  
  return response.json();
};

/**
 * Wrapper around axios for internal service calls
 * @param {Object} config - Axios request configuration
 * @returns {Promise} - Axios promise
 */
export const apiAxios = (config) => {
  const headers = {
    ...getStandardHeaders(),
    ...(config.headers || {}),
  };
  
  return axios({
    baseURL: API_BASE_URL,
    ...config,
    headers,
  });
};

/**
 * GET request wrapper using axios
 * @param {string} endpoint - API endpoint path (without base URL)
 * @param {Object} config - Additional axios config
 * @returns {Promise<any>} - Axios response
 */
export const axiosGet = (endpoint, config = {}) => {
  return apiAxios({
    method: 'get',
    url: endpoint,
    ...config,
  });
};

/**
 * POST request wrapper using axios
 * @param {string} endpoint - API endpoint path (without base URL)
 * @param {Object} data - Request body data
 * @param {Object} config - Additional axios config
 * @returns {Promise<any>} - Axios response
 */
export const axiosPost = (endpoint, data, config = {}) => {
  return apiAxios({
    method: 'post',
    url: endpoint,
    data,
    ...config,
  });
};

/**
 * PUT request wrapper using axios
 * @param {string} endpoint - API endpoint path (without base URL)
 * @param {Object} data - Request body data
 * @param {Object} config - Additional axios config
 * @returns {Promise<any>} - Axios response
 */
export const axiosPut = (endpoint, data, config = {}) => {
  return apiAxios({
    method: 'put',
    url: endpoint,
    data,
    ...config,
  });
};

/**
 * DELETE request wrapper using axios
 * @param {string} endpoint - API endpoint path (without base URL)
 * @param {Object} config - Additional axios config
 * @returns {Promise<any>} - Axios response
 */
export const axiosDelete = (endpoint, config = {}) => {
  return apiAxios({
    method: 'delete',
    url: endpoint,
    ...config,
  });
};

export default {
  apiFetch,
  apiGet,
  apiPost,
  apiPut,
  apiDelete,
  apiAxios,
  axiosGet,
  axiosPost,
  axiosPut,
  axiosDelete,
};
